// apps/admin/admin-main/src/pages/UberDeliveries/CreateQuoteDialog.tsx
import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Tabs, Tab, Box, Typography, Alert,
  CircularProgress, Stack, FormControlLabel, Checkbox, Divider,
  RadioGroup, Radio, FormControl, FormLabel,
} from '@mui/material';
import config from '../../config';
import { httpClient } from '../../components/fetchApiData';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function CreateQuoteDialog({ open, onClose }: Props) {
  const [tab, setTab] = useState(0);
  const [orderId, setOrderId] = useState('');
  const [pickup, setPickup] = useState({ street_address: [''], city: '', state: '', zip_code: '', country: 'ZA', latitude: '', longitude: '' });
  const [dropoff, setDropoff] = useState({ street_address: [''], city: '', state: '', zip_code: '', country: 'ZA', latitude: '', longitude: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quote, setQuote] = useState<any>(null);

  // Dropoff verification
  const [verificationMethod, setVerificationMethod] = useState<'pincode' | 'signature'>('pincode');
  const [dropoffPicture, setDropoffPicture] = useState(false);
  const [collectSignerName, setCollectSignerName] = useState(true);
  const [collectSignerRelationship, setCollectSignerRelationship] = useState(true);

  // Return verification
  const [returnPicture, setReturnPicture] = useState(true);
  const [returnPincode, setReturnPincode] = useState(true);

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    setQuote(null);
    try {
      const verification = {
        dropoff_verification: {
          pincode: { enabled: verificationMethod === 'pincode' },
          ...(verificationMethod === 'signature' && {
            signature: {
              enabled: true,
              collect_signer_name: collectSignerName,
              collect_signer_relationship: collectSignerRelationship,
            },
          }),
          picture: dropoffPicture,
        },
        return_verification: {
          picture: returnPicture,
          pincode: { enabled: returnPincode },
        },
      };

      const body = tab === 0
        ? { orderId, ...verification }
        : {
            pickup: { ...pickup, street_address: pickup.street_address.filter(Boolean), latitude: parseFloat(pickup.latitude) || undefined, longitude: parseFloat(pickup.longitude) || undefined },
            dropoff: { ...dropoff, street_address: dropoff.street_address.filter(Boolean), latitude: parseFloat(dropoff.latitude) || undefined, longitude: parseFloat(dropoff.longitude) || undefined },
            ...verification,
          };
      const { json } = await httpClient(`${config.API_URL}/admin/uber-deliveries/quote`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setQuote(json);
    } catch (e: any) {
      setError(e.body?.message || e.message);
    }
    setLoading(false);
  };

  const updateAddress = (
    setter: React.Dispatch<React.SetStateAction<any>>,
    field: string,
    value: string,
  ) => {
    setter((prev: any) => ({
      ...prev,
      [field]: field === 'street_address' ? [value] : value,
    }));
  };

  const AddressForm = ({ label, addr, setAddr }: { label: string; addr: any; setAddr: any }) => (
    <Box>
      <Typography variant="subtitle2" mb={1}>{label}</Typography>
      <Stack spacing={1.5}>
        <TextField label="Street Address" size="small" fullWidth value={addr.street_address[0] || ''} onChange={(e) => updateAddress(setAddr, 'street_address', e.target.value)} />
        <Stack direction="row" spacing={1}>
          <TextField label="City" size="small" fullWidth value={addr.city} onChange={(e) => updateAddress(setAddr, 'city', e.target.value)} />
          <TextField label="State/Province" size="small" fullWidth value={addr.state} onChange={(e) => updateAddress(setAddr, 'state', e.target.value)} />
        </Stack>
        <Stack direction="row" spacing={1}>
          <TextField label="Zip Code" size="small" fullWidth value={addr.zip_code} onChange={(e) => updateAddress(setAddr, 'zip_code', e.target.value)} />
          <TextField label="Country" size="small" fullWidth value={addr.country} onChange={(e) => updateAddress(setAddr, 'country', e.target.value)} />
        </Stack>
        <Stack direction="row" spacing={1}>
          <TextField label="Latitude" size="small" fullWidth type="number" value={addr.latitude} onChange={(e) => updateAddress(setAddr, 'latitude', e.target.value)} />
          <TextField label="Longitude" size="small" fullWidth type="number" value={addr.longitude} onChange={(e) => updateAddress(setAddr, 'longitude', e.target.value)} />
        </Stack>
      </Stack>
    </Box>
  );

  const handleClose = () => {
    setQuote(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Delivery Quote</DialogTitle>
      <DialogContent>
        <Tabs value={tab} onChange={(_, v) => { setTab(v); setQuote(null); setError(null); }} sx={{ mb: 2 }}>
          <Tab label="From Order" />
          <Tab label="Manual Address" />
        </Tabs>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {tab === 0 && (
          <TextField
            label="Order ID"
            fullWidth
            size="small"
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            placeholder="Paste order UUID"
          />
        )}

        {tab === 1 && (
          <Stack spacing={3}>
            <AddressForm label="Pickup Address" addr={pickup} setAddr={setPickup} />
            <AddressForm label="Dropoff Address" addr={dropoff} setAddr={setDropoff} />
          </Stack>
        )}

        <Divider sx={{ my: 2.5 }} />

        {/* Dropoff Verification */}
        <Typography variant="subtitle2" fontWeight={700} mb={1}>Dropoff Verification</Typography>
        <FormControl component="fieldset" sx={{ mb: 1 }}>
          <FormLabel component="legend" sx={{ fontSize: 13 }}>Method (Uber requires one)</FormLabel>
          <RadioGroup
            row
            value={verificationMethod}
            onChange={(e) => setVerificationMethod(e.target.value as 'pincode' | 'signature')}
          >
            <FormControlLabel value="pincode" control={<Radio size="small" />} label="PIN Code" />
            <FormControlLabel value="signature" control={<Radio size="small" />} label="Signature" />
          </RadioGroup>
        </FormControl>

        {verificationMethod === 'signature' && (
          <Stack direction="row" spacing={2} ml={1}>
            <FormControlLabel
              control={<Checkbox size="small" checked={collectSignerName} onChange={(e) => setCollectSignerName(e.target.checked)} />}
              label={<Typography variant="body2">Collect signer name</Typography>}
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={collectSignerRelationship} onChange={(e) => setCollectSignerRelationship(e.target.checked)} />}
              label={<Typography variant="body2">Collect relationship</Typography>}
            />
          </Stack>
        )}

        <FormControlLabel
          control={<Checkbox size="small" checked={dropoffPicture} onChange={(e) => setDropoffPicture(e.target.checked)} />}
          label={<Typography variant="body2">Require photo at dropoff</Typography>}
        />

        <Divider sx={{ my: 2 }} />

        {/* Return Verification */}
        <Typography variant="subtitle2" fontWeight={700} mb={1}>Return Verification</Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          If delivery fails and package is returned to store
        </Typography>
        <Stack direction="row" spacing={2}>
          <FormControlLabel
            control={<Checkbox size="small" checked={returnPicture} onChange={(e) => setReturnPicture(e.target.checked)} />}
            label={<Typography variant="body2">Photo proof</Typography>}
          />
          <FormControlLabel
            control={<Checkbox size="small" checked={returnPincode} onChange={(e) => setReturnPincode(e.target.checked)} />}
            label={<Typography variant="body2">PIN code</Typography>}
          />
        </Stack>

        {quote && (
          <Box mt={2} p={2} bgcolor="#f0f7f0" borderRadius={1}>
            <Typography variant="subtitle2" color="success.main" gutterBottom>Quote Created</Typography>
            <Typography variant="body2">Fee: R{((quote.fee || 0) / 100).toFixed(2)}</Typography>
            {quote.estimated_delivery_date && (
              <Typography variant="body2">ETA: {new Date(quote.estimated_delivery_date).toLocaleString('en-ZA')}</Typography>
            )}
            {quote.expires_at && (
              <Typography variant="caption" color="text.secondary">
                Expires: {new Date(quote.expires_at).toLocaleString('en-ZA')}
              </Typography>
            )}
            <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
              Quote ID: {quote.id}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || (tab === 0 && !orderId)}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          Get Quote
        </Button>
      </DialogActions>
    </Dialog>
  );
}
